import { Button } from "@/components/ui/button";
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList } from "@/components/ui/combobox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import React from "react";
import type { SelectedTPAFields, TPARulesPhase } from "./details-section/useTPARules";
import { useGetCarriersQuery, useGetTPAsQuery } from "@/hooks/queries/organization";
import { Loader2 } from "lucide-react";

interface TPARulesConfirmationModalProps {
  open: boolean;
  phase: TPARulesPhase;
  onClose: () => void;
  onConfirm: () => void;
  selectedTPAFields: SelectedTPAFields;
  updateSelectedTPAField: (field: keyof SelectedTPAFields, value: string) => void;
}

export default function TPARulesConfirmationModal({
  open,
  phase,
  onClose,
  onConfirm,
  selectedTPAFields,
  updateSelectedTPAField,
}: TPARulesConfirmationModalProps) {
  const isSaving = phase === "saving";
  const isLocked = phase === "saving" || phase === "syncing";
  // Block dismissal while the change is saving or the worker is still recomputing line items — but never
  // while showing the failure, or the user would be stuck in the dialog.
  const onOpenChange = (nextOpen: boolean) => {
    if (nextOpen) return;
    if (isLocked) return;
    onClose();
  };
  const { data: carriers } = useGetCarriersQuery();
  const carrierOptions = carriers
    ?.map((carrier) => ({
      value: carrier.id,
      label: carrier.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const { data: tpas } = useGetTPAsQuery();
  const tpaOptions = tpas
    ?.map((tpa) => ({
      value: tpa.id,
      label: tpa.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const getMessage = () => {
    const parts = [];
    if (selectedTPAFields.tpaId) {
      const tpaName = tpaOptions?.find((tpaOption) => tpaOption.value === selectedTPAFields.tpaId)?.label;
      parts.push(
        <span>
          TPA <span className="font-bold">{tpaName ?? ""}</span>
        </span>,
      );
    }
    if (selectedTPAFields.carrierId) {
      const carrierName = carrierOptions?.find((carrierOption) => carrierOption.value === selectedTPAFields.carrierId)?.label;
      parts.push(
        <span>
          Carrier <span className="font-bold">{carrierName ?? ""}</span>
        </span>,
      );
    }

    return (
      <p className="text-sm text-gray-800">
        Do you want to apply program rules with{" "}
        {parts.map((part, index) => (
          <React.Fragment key={index}>
            {part}
            {index < parts.length - 1 ? " and " : ""}
          </React.Fragment>
        ))}
        ? Your edits will be reverted when switching TPA
      </p>
    );
  };

  const selectedTpaOption = tpaOptions?.find((opt) => opt.value === selectedTPAFields.tpaId) ?? null;
  const selectedCarrierOption = carrierOptions?.find((opt) => opt.value === selectedTPAFields.carrierId) ?? null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        hideCloseButton={isLocked}
        onEscapeKeyDown={(e) => isLocked && e.preventDefault()}
        onInteractOutside={(e) => isLocked && e.preventDefault()}
        className="flex w-full max-w-md flex-col rounded-lg !bg-white p-6 shadow-lg"
      >
        {phase === "syncing" ? (
          <DialogHeader>
            <DialogTitle className="text-lg font-medium text-gray-800">Applying program rules…</DialogTitle>
            <DialogDescription className="mt-2 flex items-center gap-2 text-sm text-gray-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              We&apos;re updating your line items. This might take a moment.
            </DialogDescription>
          </DialogHeader>
        ) : phase === "failed" ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-lg font-medium text-red-600">Couldn&apos;t apply program rules</DialogTitle>
              <DialogDescription className="mt-2 text-sm text-gray-600">
                The TPA/carrier change wasn&apos;t applied and your line items are unchanged. You can try again.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-6 flex flex-row-reverse gap-2">
              <Button variant="accent" onClick={onConfirm}>
                Try again
              </Button>
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-base leading-4 font-medium">Confirm TPA Rules</DialogTitle>
            </DialogHeader>

            <p className="text-sm text-gray-800">{getMessage()}</p>
            <div className="mt-4 flex flex-col gap-4">
              {!selectedTPAFields.tpaId && (
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium">Select TPA</label>
                  <div className="relative w-full">
                    <Combobox
                      items={tpaOptions ?? []}
                      itemToStringValue={(option) => option.label}
                      value={selectedTpaOption}
                      onValueChange={(item) => updateSelectedTPAField("tpaId", item?.value ?? "")}
                    >
                      <ComboboxInput showClear={!!selectedTPAFields.tpaId} disabled={isSaving} placeholder="Select TPA" />
                      <ComboboxContent className="pointer-events-auto">
                        <ComboboxEmpty>No TPAs available</ComboboxEmpty>
                        <ComboboxList className="max-h-60 overflow-y-auto">
                          {(option) => (
                            <ComboboxItem key={option.value} value={option}>
                              {option.label}
                            </ComboboxItem>
                          )}
                        </ComboboxList>
                      </ComboboxContent>
                    </Combobox>
                  </div>
                </div>
              )}

              {!selectedTPAFields.carrierId && (
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium">Select Carrier</label>
                  <div className="relative w-full">
                    <Combobox
                      items={carrierOptions ?? []}
                      itemToStringValue={(option) => option.label}
                      value={selectedCarrierOption}
                      onValueChange={(item) => updateSelectedTPAField("carrierId", item?.value ?? "")}
                    >
                      <ComboboxInput showClear={!!selectedTPAFields.carrierId} disabled={isSaving} placeholder="Select Carrier" />
                      <ComboboxContent className="pointer-events-auto">
                        <ComboboxEmpty>No carriers available</ComboboxEmpty>
                        <ComboboxList className="max-h-60 overflow-y-auto">
                          {(option) => (
                            <ComboboxItem key={option.value} value={option}>
                              {option.label}
                            </ComboboxItem>
                          )}
                        </ComboboxList>
                      </ComboboxContent>
                    </Combobox>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex w-full flex-row justify-end gap-2 self-end">
              <Button variant="outline" onClick={onClose} disabled={isSaving}>
                Cancel
              </Button>
              <Button variant="accent" onClick={onConfirm} disabled={isSaving}>
                {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                Apply Rules
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
